import React from 'react';
import { Star, CheckCircle2, Check } from 'lucide-react';
import { useTranslations } from '../hooks/useTranslations';
export function Hero() {
  const {
    t
  } = useTranslations();
  return <section id="hero" className="relative pt-24 pb-16 lg:pt-28 lg:pb-20 overflow-hidden bg-white" data-id="element-133">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-134">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-135">
          {/* Left Content */}
          <div className="space-y-6 text-center lg:text-left max-w-2xl mx-auto lg:mx-0" data-id="element-136">
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-gray-900 leading-[1.1] tracking-tight" data-id="element-137">
              {t('hero.title')}
            </h1>

            <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-lg mx-auto lg:mx-0" data-id="element-138">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col items-center lg:items-start gap-3" data-id="element-139">
              {/* Primary CTA */}
              <a href="https://form.jotform.com/260402880146048" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center text-sm font-bold px-6 py-3 rounded-lg text-white bg-[#00E676] shadow-lg shadow-green-200/50 hover:bg-[#00d169] hover:shadow-xl hover:shadow-green-300/50 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 ease-out animate-hero-pulse" data-id="element-140">
                {t('hero.cta')}
              </a>

              <p className="text-sm text-gray-600" data-id="element-141">
                Para chatear con un especialista, haz clic en el ícono de chat
                💬
              </p>

              {/* Microcopy Trust Signals */}
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-sm text-gray-600" data-id="element-142">
                {t('hero.microcopy1') && <div className="flex items-center gap-1.5" data-id="element-143">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-144" />
                    <span data-id="element-145">{t('hero.microcopy1')}</span>
                  </div>}
                {t('hero.microcopy2') && <div className="flex items-center gap-1.5" data-id="element-146">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-147" />
                    <span data-id="element-148">{t('hero.microcopy2')}</span>
                  </div>}
                {t('hero.microcopy3') && <div className="flex items-center gap-1.5" data-id="element-149">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-150" />
                    <span data-id="element-151">{t('hero.microcopy3')}</span>
                  </div>}
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="pt-4 flex flex-col items-center lg:items-start gap-4" data-id="element-152">
              {/* Row 1: Stars + Trustpilot */}
              <div className="flex items-center gap-2" data-id="element-153">
                <div className="flex" data-id="element-154">
                  {[1, 2, 3, 4].map(i => <Star key={i} className="w-5 h-5 text-[#00b67a] fill-current" data-id="element-155" />)}
                  <div className="relative w-5 h-5" data-id="element-156">
                    <Star className="w-5 h-5 text-gray-300 fill-current absolute inset-0" data-id="element-157" />
                    <div className="absolute inset-0 overflow-hidden" style={{
                    width: '80%'
                  }} data-id="element-158">
                      <Star className="w-5 h-5 text-[#00b67a] fill-current" data-id="element-159" />
                    </div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-160">
                  {t('hero.trustpilot')}
                </span>
              </div>

              {/* Row 2: Avatars + Clients */}
              <div className="flex items-center text-sm font-medium text-gray-600" data-id="element-161">
                <div className="flex -space-x-2 mr-3" data-id="element-162">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 overflow-hidden" data-id="element-163">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-164" />
                    </div>)}
                </div>
                {t('hero.trustedBy')}
              </div>
            </div>
          </div>

          {/* Right Content - Image & Background Shape */}
          <div className="relative lg:h-[500px] flex items-center justify-center mt-10 lg:mt-0" data-id="element-165">
            {/* Green Geometric Background */}
            <div className="absolute inset-0 z-0 flex justify-center items-center" data-id="element-166">
              <div className="w-[120%] h-[120%] bg-gradient-to-tr from-[#e6fcf0] to-[#b3f5d1] transform rotate-12 skew-x-12 rounded-[100px] opacity-80" data-id="element-167"></div>
            </div>

            <div className="relative w-full max-w-md lg:max-w-none z-10" data-id="element-168">
              <img src="https://cdn.magicpatterns.com/uploads/kGkyYG1wwa3dggLJRfv8Sm/vecteezy_business-woman-in-glasses-using-her-phone-on-transparent_47308872.png" alt="Professional Business Executive" className="w-full h-auto object-contain relative z-10 scale-110" data-id="element-169" />

              {/* Floating Approval Badge 1 */}
              <div className="absolute -top-2 right-0 md:right-8 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2 z-20" data-id="element-170">
                <CheckCircle2 className="w-4 h-4 text-[#00E676]" data-id="element-171" />
                <p className="text-xs font-bold text-gray-900" data-id="element-172">
                  {t('hero.approvalBadge1')}
                </p>
              </div>

              {/* Floating Approval Badge 2 */}
              <div className="absolute top-1/2 -left-4 md:left-0 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2 z-20" data-id="element-173">
                <CheckCircle2 className="w-4 h-4 text-[#00E676]" data-id="element-174" />
                <p className="text-xs font-bold text-gray-900" data-id="element-175">
                  {t('hero.approvalBadge2')}
                </p>
              </div>

              {/* Floating Approval Badge 3 */}
              <div className="absolute bottom-10 right-0 md:right-10 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2 z-20" data-id="element-176">
                <CheckCircle2 className="w-4 h-4 text-[#00E676]" data-id="element-177" />
                <p className="text-xs font-bold text-gray-900" data-id="element-178">
                  {t('hero.approvalBadge3')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}